import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { db, auth } from '../lib/firebase';
import { collection, getDocs, doc, getDoc, query, orderBy, limit, where } from 'firebase/firestore';
import { useSEO } from '../hooks/useSEO';
import { blogPosts } from '../data/blogPosts';

// ── Couleurs cartes matières (accent discret sur fond blanc) ─────────────
const SUBJECT_CARD_STYLES = {
  maths:        { accent: '#3b82f6' },
  francais:     { accent: '#e11d48' },
  anglais:      { accent: '#d97706' },
  physique:     { accent: '#7c3aed' },
  creole:       { accent: '#0d9488' },
  svt:          { accent: '#16a34a' },
  informatique: { accent: '#475569' },
  musique:      { accent: '#db2777' },
};

// ── Couleurs cartes territoires (palette sombre cohérente) ───────────────
const TERRITORY_STYLES = {
  '🇬🇵 Guadeloupe':                   { bg: 'linear-gradient(135deg,#1e3a5f,#0f2340)' },
  '🇲🇶 Martinique':                   { bg: 'linear-gradient(135deg,#14432a,#0a2918)' },
  '🇬🇫 Guyane':                       { bg: 'linear-gradient(135deg,#1a4731,#0e2e1e)' },
  '🇷🇪 La Réunion':                   { bg: 'linear-gradient(135deg,#4a2008,#2d1305)' },
  '🇾🇹 Mayotte':                      { bg: 'linear-gradient(135deg,#0c3559,#071e35)' },
  '🇭🇹 Haïti':                        { bg: 'linear-gradient(135deg,#1a2e5e,#0e1b3a)' },
  '🇩🇴 Rép. Dominicaine':             { bg: 'linear-gradient(135deg,#1e2060,#12143d)' },
  '🇯🇲 Jamaïque':                     { bg: 'linear-gradient(135deg,#2a3a10,#192208)' },
  '🇹🇹 Trinidad':                     { bg: 'linear-gradient(135deg,#3d1010,#220808)' },
  '🇵🇫 Polynésie française':           { bg: 'linear-gradient(135deg,#0c3545,#07202b)' },
  '🇳🇨 Nouvelle-Calédonie':           { bg: 'linear-gradient(135deg,#0f3535,#082020)' },
  '🏝️ Saint-Martin / Saint-Barth':    { bg: 'linear-gradient(135deg,#3d1535,#220a1e)' },
  '🌐 En ligne':                       { bg: 'linear-gradient(135deg,#1e1560,#110d3a)' },
};

// ── Pastille "Prof certifié" (≥ 5 avis) ─────────────────────────────────
function CertifiedBadge({ className = '' }) {
  return (
    <span
      title="Prof certifié EduKaraib — plus de 5 avis vérifiés"
      className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400 text-white text-[10px] font-bold shadow-sm shrink-0 " + className}
    >
      🏅 Certifié
    </span>
  );
}

// ── Pastille "Nouveau prof" (inscrit il y a < 30 jours) ──────────────────
function NewProfBadge({ teacher, className = '' }) {
  const raw = teacher.createdAt;
  if (!raw) return null;
  const date = raw?.toDate ? raw.toDate() : new Date((raw?.seconds ?? 0) * 1000);
  if (Date.now() - date.getTime() > 30 * 24 * 60 * 60 * 1000) return null;
  return (
    <span
      title="Nouveau professeur sur EduKaraib"
      className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500 text-white text-[10px] font-bold shadow-sm shrink-0 " + className}
    >
      ✨ Nouveau
    </span>
  );
}



// ── Carrousel avis auto-défilant (jusqu'à 10 avis, mobile + desktop) ──────
function ReviewCarousel({ reviews, teacherMap, getSubjectLabel, reviewBgClass }) {
  const [current, setCurrent] = React.useState(0);
  const total = Math.min(10, reviews.length);
  const timerRef = React.useRef(null);

  const go = React.useCallback((idx) => {
    setCurrent(((idx % total) + total) % total);
  }, [total]);

  // Auto-scroll toutes les 4s
  React.useEffect(() => {
    if (total <= 1) return;
    timerRef.current = setInterval(() => setCurrent(c => (c + 1) % total), 4000);
    return () => clearInterval(timerRef.current);
  }, [total]);

  const resetTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCurrent(c => (c + 1) % total), 4000);
  };

  const prev = () => { go(current - 1); resetTimer(); };
  const next = () => { go(current + 1); resetTimer(); };

  if (total === 0) return null;

  const reviewItems = reviews.slice(0, total).map((r) => {
    const t = r.teacher_id ? teacherMap.get(r.teacher_id) : null;
    const parentName = (r.fullName || r.userName || r.reviewerName || '').trim() || 'Parent';
    const avatar = r.userAvatar || r.avatarUrl || r.photoURL || '/avatar-default.png';
    const profName = r._teacherFullName ||
      (t?.fullName || t?.name || [t?.firstName, t?.lastName].filter(Boolean).join(' ') || '').trim() ||
      'Professeur';
    const profSubject = (typeof r._teacherSubjects === 'string' && r._teacherSubjects.trim()
      ? r._teacherSubjects.trim()
      : Array.isArray(r._teacherSubjects) && r._teacherSubjects.length
        ? r._teacherSubjects.filter(Boolean).join(', ')
        : null) || getSubjectLabel(t || {});
    const starsCount = Math.max(0, Math.min(5, Math.round(Number(r.rating) || 0)));
    const stars = '★'.repeat(starsCount) + '☆'.repeat(5 - starsCount);
    return { r, parentName, avatar, profName, profSubject, stars };
  });

  return (
    <div className="relative">
      {/* MOBILE : slider natif horizontal */}
      <div className="md:hidden -mx-4 px-4 overflow-x-auto pb-3 snap-x snap-mandatory flex gap-4">
        {reviewItems.map(({ r, parentName, avatar, profName, profSubject, stars }) => (
          <div key={r.id} className={`snap-start shrink-0 w-[86%] max-w-[340px] rounded-3xl p-5 ${reviewBgClass(r.id)}`}>
            <div className="flex items-start gap-3">
              <img src={avatar} alt={parentName} className="h-12 w-12 rounded-full object-cover border border-white/60 shrink-0" />
              <div className="min-w-0">
                <div className="font-extrabold text-gray-900 leading-tight truncate">{parentName}</div>
                <div className="text-xs text-gray-500 mt-0.5">{profName} <span className="font-normal">(Prof {profSubject})</span></div>
              </div>
            </div>
            <p className="mt-3 text-gray-900/90 text-sm leading-relaxed line-clamp-4">{r.comment}</p>
            <div className="mt-3 flex items-center gap-1.5">
              <span className="text-amber-500 text-sm">{stars}</span>
              <span className="text-gray-500 text-xs">{Number(r.rating || 0).toFixed(0)}/5</span>
            </div>
          </div>
        ))}
      </div>

      {/* DESKTOP : 3 cartes en ligne + flèches + points de navigation */}
      <div className="hidden md:block">
        {/* 3 cartes visibles en même temps */}
        <div className="grid grid-cols-3 gap-6">
          {[0, 1, 2].map((offset) => {
            const idx = (current + offset) % total;
            const { r, parentName, avatar, profName, profSubject, stars } = reviewItems[idx];
            return (
              <div key={r.id + '-' + idx} className={`rounded-3xl p-6 transition-all duration-500 ${reviewBgClass(r.id)}`}>
                <div className="flex items-start gap-3">
                  <img src={avatar} alt={parentName} className="h-12 w-12 rounded-full object-cover border border-white/60 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-extrabold text-gray-900 leading-tight truncate">{parentName}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{profName} <span className="font-normal">(Prof {profSubject})</span></div>
                  </div>
                </div>
                <p className="mt-3 text-gray-900/90 text-sm leading-relaxed line-clamp-4">{r.comment}</p>
                <div className="mt-4 flex items-center gap-1.5">
                  <span className="text-amber-500 text-sm font-semibold">{stars}</span>
                  <span className="text-gray-500 text-xs">{Number(r.rating || 0).toFixed(0)}/5</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Flèches + points */}
        {total > 3 && (
          <div className="mt-6 flex items-center justify-center gap-4">
            <button onClick={prev} className="w-9 h-9 rounded-full border border-gray-300 bg-white hover:bg-gray-50 flex items-center justify-center text-gray-600 shadow-sm transition">‹</button>
            <div className="flex gap-2">
              {Array.from({ length: total }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => { go(i); resetTimer(); }}
                  className={"w-2 h-2 rounded-full transition " + (i === current ? 'bg-primary w-5' : 'bg-gray-300 hover:bg-gray-400')}
                />
              ))}
            </div>
            <button onClick={next} className="w-9 h-9 rounded-full border border-gray-300 bg-white hover:bg-gray-50 flex items-center justify-center text-gray-600 shadow-sm transition">›</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();

  // ── Redirection admin si connecté ─────────────────────────────
  useEffect(() => {
    const checkRole = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        const role = snap.exists() ? snap.data()?.role : null;
        if (role === 'admin') navigate('/admin/dashboard');
      } catch (e) {
        console.warn('Role check failed', e);
      }
    };
    checkRole();
  }, [navigate]);

  // ── Barre de recherche ────────────────────────────────────────
  const [qSubject, setQSubject] = useState('');
  const [qCity, setQCity] = useState('');
  const [qLevel, setQLevel] = useState('');

  const onSearch = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (qSubject.trim()) params.set('subject', qSubject.trim());
    if (qCity.trim()) params.set('city', qCity.trim());
    if (qLevel.trim()) params.set('level', qLevel.trim());
    navigate(`/search?${params.toString()}`);
  };

  // ── Professeurs + agrégation notes (chargement unique) ──────────
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [teacherMap, setTeacherMap] = useState(new Map());

  useEffect(() => {
    const run = async () => {
      setLoadingTeachers(true);
      try {
        // Vitrine publique des profs (copie sans e-mail ni téléphone, 23/10/2026)
        let all = [];
        {
          const qUsersTeachers = query(
            collection(db, 'profils'),
            where('role', '==', 'teacher'),
            limit(100)
          );
          const uSnap = await getDocs(qUsersTeachers);
          all = uSnap.docs.map((d) => {
            const u = d.data();
            return {
              id: d.id,
              ...u,
              fullName:
                u.fullName ||
                u.name ||
                [u.firstName, u.lastName].filter(Boolean).join(' ') ||
                'Professeur',
              avatarUrl: u.avatarUrl || u.photoURL,
              location: u.city || u.location,
              subjects: u.subjects ?? u.subjectsLabel ?? u.subject ?? u.main_subject,
              price_per_hour: u.price_per_hour || u.price,
              rating: u.rating,
            };
          });
        }

        all = (all || []).filter((t) => t.offer_enabled !== false);

        // 3) Agrégation notes en parallèle (chunks de 10)
        const ids = all.map(t => t.id).filter(Boolean);
        const chunks = [];
        for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

        const stats = new Map();
        await Promise.all(chunks.map(async (chunk) => {
          try {
            const snap = await getDocs(query(collection(db, 'reviews'), where('teacher_id', 'in', chunk)));
            snap.docs.forEach(d => {
              const r = d.data();
              const tid = r.teacher_id;
              const rating = Number(r.rating || 0);
              if (!tid || rating <= 0) return;
              const prev = stats.get(tid) || { sum: 0, count: 0 };
              prev.sum += rating;
              prev.count += 1;
              stats.set(tid, prev);
            });
          } catch {}
        }));

        // 4) Enrichir + trier
        const enriched = all.map(t => {
          const s = stats.get(t.id);
          return s
            ? { ...t, _avgRating: s.sum / s.count, _reviewsCount: s.count }
            : { ...t, _avgRating: 0, _reviewsCount: 0 };
        });

        const withMeta = enriched.map((p) => ({
          ...p,
          _rating: Number(p._avgRating ?? p.avgRating ?? p.rating ?? 0),
        }));
        const top = withMeta.filter((p) => p._rating >= 4.7).sort((a, b) => b._rating - a._rating);
        const rest = withMeta.filter((p) => p._rating < 4.7);
        for (let i = rest.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [rest[i], rest[j]] = [rest[j], rest[i]];
        }
        const arranged = [...top, ...rest];

        setTeachers(arranged);
        const map = new Map();
        arranged.forEach((t) => map.set(t.id, t));
        setTeacherMap(map);
      } catch (e) {
        console.error('Chargement des profs échoué:', e);
        setTeachers([]);
      } finally {
        setLoadingTeachers(false);
      }
    };
    run();
  }, []);

  // ── Avis (réels) — batch lookup reviewer + prof ───────────────
  const [reviews, setReviews] = useState([]);
  const [hasRealReviews, setHasRealReviews] = useState(false);

  useEffect(() => {
    const run = async () => {
      try {
        const qReviews = query(
          collection(db, 'reviews'),
          orderBy('created_at', 'desc'),
          limit(12)
        );
        let list = (await getDocs(qReviews)).docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) => (r.comment?.trim()?.length ?? 0) > 0 && Number(r.rating) > 0);

        // Collecter tous les UIDs uniques à résoudre
        const reviewerIds = new Set();
        const teacherIds = new Set();
        list.forEach(r => {
          if (!(r.fullName || r.userName || r.reviewerName || r.userAvatar || r.photoURL)) {
            const rid = r.user_id || r.student_id || r.parent_id;
            if (rid) reviewerIds.add(rid);
          }
          if (r.teacher_id && !(r._teacherFullName || r._teacherSubjects)) {
            teacherIds.add(r.teacher_id);
          }
        });

        // Batch fetch en parallèle
        const allIds = [...new Set([...reviewerIds, ...teacherIds])];
        const userCache = new Map();
        await Promise.all(allIds.map(async (uid) => {
          try {
            const snap = await getDoc(doc(db, 'profils', uid));
            if (snap.exists()) userCache.set(uid, snap.data());
          } catch {}
        }));

        // Enrichir les avis depuis le cache
        list = list.map(r => {
          if (!(r.fullName || r.userName || r.reviewerName || r.userAvatar || r.photoURL)) {
            const rid = r.user_id || r.student_id || r.parent_id;
            const u = rid ? userCache.get(rid) : null;
            if (u) {
              r.fullName = r.fullName || u.fullName || u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Utilisateur';
              r.userAvatar = r.userAvatar || u.avatarUrl || u.photoURL;
            }
          }
          if (r.teacher_id && !(r._teacherFullName || r._teacherSubjects)) {
            const t = userCache.get(r.teacher_id);
            if (t) {
              r._teacherFullName = t.fullName || t.name || [t.firstName, t.lastName].filter(Boolean).join(' ') || 'Professeur';
              r._teacherSubjects = t.subjects ?? t.subject ?? t.main_subject ?? '';
            }
          }
          return r;
        });

        setReviews(list);
        setHasRealReviews((list?.length || 0) > 0);
      } catch (e) {
        console.error('Chargement des avis échoué:', e);
        setHasRealReviews(false);
      }
    };
    run();
  }, []);

  // ── Catégories / Villes ──────────────────────────────────────
  const categories = useMemo(
    () => [
      { slug: 'maths', label: 'Mathématiques', emoji: '📐' },
      { slug: 'francais', label: 'Français', emoji: '📖' },
      { slug: 'anglais', label: 'Anglais', emoji: '🗣️' },
      { slug: 'physique', label: 'Physique-Chimie', emoji: '⚗️' },
      { slug: 'creole', label: 'Créole', emoji: '🌴' },
      { slug: 'svt', label: 'SVT', emoji: '🧬' },
      { slug: 'informatique', label: 'Informatique', emoji: '💻' },
      { slug: 'musique', label: 'Musique', emoji: '🎶' },
    ],
    []
  );

  const cityGroups = useMemo(
    () => [
      { label: '🇬🇵 Guadeloupe', cities: ['Pointe-à-Pitre', 'Les Abymes', 'Baie-Mahault', 'Le Gosier', 'Basse-Terre', 'Sainte-Anne', 'Le Moule', 'Capesterre-Belle-Eau'] },
      { label: '🇲🇶 Martinique', cities: ['Fort-de-France', 'Le Lamentin', 'Le Robert', 'Schoelcher', 'Le François', 'Sainte-Marie', 'Le Marin', 'La Trinité'] },
      { label: '🇬🇫 Guyane', cities: ['Cayenne', 'Kourou', 'Matoury', 'Rémire-Montjoly', 'Saint-Laurent-du-Maroni', 'Macouria', 'Mana', 'Maripasoula'] },
      { label: '🇷🇪 La Réunion', cities: ['Saint-Denis', 'Saint-Paul', 'Saint-Pierre', 'Le Tampon', 'Saint-Louis', 'Saint-André', 'Saint-Benoît', 'La Possession'] },
      { label: '🇾🇹 Mayotte', cities: ['Mamoudzou', 'Koungou', 'Bandraboua', 'Dzaoudzi', 'Bouéni'] },
      { label: '🏝️ Saint-Martin / Saint-Barth', cities: ['Marigot', 'Grand-Case', 'Philipsburg', 'Gustavia'] },
      { label: '🇭🇹 Haïti', cities: ['Port-au-Prince', 'Pétionville', 'Cap-Haïtien', 'Delmas', 'Carrefour', 'Gonaïves'] },
      { label: '🇩🇴 Rép. Dominicaine', cities: ['Saint-Domingue', 'Santiago de los Caballeros', 'La Romana', 'Puerto Plata'] },
      { label: '🇯🇲 Jamaïque', cities: ['Kingston', 'Montego Bay', 'Spanish Town'] },
      { label: '🇹🇹 Trinidad', cities: ['Port of Spain', 'San Fernando', 'Chaguanas'] },
      { label: '🇵🇫 Polynésie française', cities: ['Papeete', 'Faaa', 'Punaauia', 'Pirae'] },
      { label: '🇳🇨 Nouvelle-Calédonie', cities: ['Nouméa', 'Mont-Dore', 'Dumbéa', 'Païta'] },
      { label: '🌐 En ligne', cities: ['En ligne'] },
    ],
    []
  );

  const cities = useMemo(() => cityGroups.flatMap(g => g.cities), [cityGroups]);

  const getModesLabel = (t) => {
    const pres = !!t.presentiel_enabled;
    const visio = !!t.visio_enabled;
    if (pres && visio) return 'Visio + Présentiel';
    if (pres) return 'Présentiel';
    if (visio) return 'Visio';
    return '';
  };

  const getDisplayNameParts = (t) => {
    const fn = (t.firstName || '').trim();
    const ln = (t.lastName || '').trim();

    if (fn || ln) return { first: fn || 'Prof', last: ln || '' };

    const full = (t.fullName || t.name || 'Professeur').trim();
    const parts = full.split(' ').filter(Boolean);
    if (parts.length <= 1) return { first: full, last: '' };

    return { first: parts[0], last: parts.slice(1).join(' ') };
  };

  const getReviewCount = (t) =>
    Number(t._reviewsCount ?? t.reviewsCount ?? t.reviews_count ?? t.nbReviews ?? t.countReviews ?? 0);

  const getRating = (t) =>
    Number(t._avgRating ?? t.avgRating ?? t.rating ?? 0);

  const COMMISSION = 10;

  const getPriceLines = (t) => {
    const pres = !!t.presentiel_enabled;
    const visio = !!t.visio_enabled;

    const presPrice = Number(t.price_per_hour ?? t.presentiel_price_per_hour ?? 0);
    const visioPrice = Number(t.visio_price_per_hour ?? 0);

    const lines = [];

    if (pres && presPrice > 0) {
      lines.push({
        label: 'Présentiel',
        price: presPrice + COMMISSION,
      });
    }

    if (visio && visioPrice > 0) {
      lines.push({
        label: 'Visio',
        price: visioPrice + COMMISSION,
      });
    }

    // fallback si aucun mode détecté
    if (!lines.length && presPrice > 0) {
      lines.push({
        label: 'Cours',
        price: presPrice + COMMISSION,
      });
    }

    return lines;
  };

  const getSubjectLabel = (t) => {
    const s = t.subjects ?? t.subject ?? t.main_subject;

    if (typeof s === 'string' && s.trim()) return s.trim();
    if (Array.isArray(s) && s.length) return s.filter(Boolean).join(', ');

    return 'Matière non spécifiée';
  };

  // Compte le nb de profs par matière (slug → count)
  const subjectCount = useMemo(() => {
    const map = {};
    (teachers || []).forEach((t) => {
      const s = t.subjects ?? t.subject ?? t.main_subject;
      const arr = Array.isArray(s) ? s : (typeof s === 'string' && s ? [s] : []);
      arr.forEach((sub) => {
        const norm = sub.trim().toLowerCase();
        map[norm] = (map[norm] || 0) + 1;
      });
    });
    return map;
  }, [teachers]);

  const getSubjectCount = (label) => {
    const norm = label.trim().toLowerCase();
    // cherche une correspondance partielle
    let total = 0;
    Object.entries(subjectCount).forEach(([k, v]) => {
      if (k.includes(norm) || norm.includes(k)) total += v;
    });
    return total;
  };

  const reviewBgClass = (id) => {
    const colors = ['bg-yellow-100', 'bg-green-100', 'bg-blue-100'];
    let hash = 0;
    const s = String(id || '');
    for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
    return colors[hash % colors.length];
  };

  useSEO({
    title: 'Cours particuliers aux Caraïbes',
    description: 'Trouvez un professeur particulier aux Caraïbes pour vos enfants ou vous-même. Maths, Français, Anglais et plus. Réservez en ligne sur EduKaraib.',
    url: '/',
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* HERO */}
      <header className="relative isolate overflow-hidden">
        {/* Image desktop uniquement */}
        <img
          src="/hero-promo-2026.png"
          alt="Offre nouvelle année EduKaraib"
          className="hidden lg:block absolute inset-0 h-full w-full object-cover object-right"
        />

        {/* Overlay flou desktop uniquement */}
        <div className="hidden lg:block absolute inset-0 bg-white/60 backdrop-blur-sm" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-28">
        {/* HERO MOBILE : image + texte superposé (SANS bloc) */}
        <div className="lg:hidden relative rounded-3xl overflow-hidden mb-8">
          {/* Image */}
          <div className="relative min-h-[420px]">
            <img
              src="/hero-promo-2026.png"
              alt="Offre nouvelle année EduKaraib"
              className="absolute inset-0 w-full h-full object-cover object-[82%_center] blur-[1px] scale-[1.02]"
            />

            {/* voile pour lisibilité */}
            <div className="absolute inset-0 bg-white/35" />

            {/* Texte directement sur l'image */}
            <div className="relative px-5 pt-6 pb-6">
              <span className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full bg-primary text-white text-sm font-semibold">
                🎓 Cours particuliers aux Caraïbes
              </span>

              <h1 className="text-4xl font-extrabold text-gray-900 leading-tight">
                Trouvez le bon prof,
                <span className="block text-primary">où que vous soyez</span>
              </h1>

              <p className="mt-3 text-base text-gray-800 max-w-[32ch]">
                Des professeurs vérifiés dans toute la Caraïbe, en présentiel ou visio. Réservez en quelques clics.
              </p>

              <ul className="mt-4 space-y-2 text-base text-gray-800">
                <li className="flex items-center gap-2"><span className="text-primary font-bold">✔</span> Professeurs vérifiés aux Caraïbes</li>
                <li className="flex items-center gap-2"><span className="text-primary font-bold">✔</span> Présentiel ou visio</li>
                <li className="flex items-center gap-2"><span className="text-primary font-bold">✔</span> Paiement sécurisé, remboursement possible</li>
              </ul>
              {/* 🔴 CTA MOBILE */}
              <div className="mt-6">
                <Link to="/search" className="inline-block bg-green-500 hover:bg-green-600 text-white font-bold text-lg px-8 py-4 rounded-2xl shadow-lg transition">
                  🔎 Trouver un prof →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* 🎟️ BANDEROLE INSCRIPTION - MOBILE, entre hero image et formulaire */}
        <div className="lg:hidden mx-0 -mx-4 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white flex items-center gap-3 mb-4">
          <span className="text-2xl shrink-0">🎁</span>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm leading-tight">-5 € sur votre premier cours</div>
            <div className="text-xs text-white/80">Offert automatiquement dès l'inscription</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href="/register"
              className="bg-white text-emerald-700 font-bold text-[11px] px-3 py-1.5 rounded-full hover:bg-emerald-50 whitespace-nowrap"
            >
              S'inscrire →
            </a>
          </div>
        </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="hidden lg:block">
              <span className="inline-block mb-4 px-4 py-2 rounded-full bg-primary text-white text-sm font-semibold">
                🎓 Cours particuliers aux Caraïbes
              </span>

              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900">
                Trouvez le bon prof,
                <span className="block text-primary">où que vous soyez</span>
              </h1>

              <p className="mt-4 text-lg text-gray-700 max-w-xl">
                Des professeurs vérifiés dans toute la Caraïbe, en présentiel ou visio. Réservez en quelques clics.
              </p>

              <ul className="mt-6 space-y-2 text-gray-700">
                <li>✔ Professeurs vérifiés aux Caraïbes</li>
                <li>✔ Présentiel ou visio</li>
                <li>✔ Paiement sécurisé, remboursement possible</li>
              </ul>
              {/* 🔴 CTA DESKTOP */}
              <div className="mt-8">
                <Link to="/search" className="inline-block bg-green-500 hover:bg-green-600 text-white font-bold text-xl px-10 py-5 rounded-2xl shadow-xl transition">
                  🔎 Trouver un prof →
                </Link>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur rounded-3xl shadow-xl border border-gray-100 p-4 md:p-6 mt-6 lg:mt-12">
              <form onSubmit={onSearch} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input
                  className="border rounded-2xl px-4 py-3 w-full text-base md:text-lg"
                  placeholder="Quelle matière ? (ex: Mathématiques)"
                  value={qSubject}
                  onChange={(e) => setQSubject(e.target.value)}
                />
                <input
                  className="border rounded-2xl px-4 py-3 w-full text-base md:text-lg"
                  placeholder="Ville / commune (ex: Cayenne)"
                  value={qCity}
                  onChange={(e) => setQCity(e.target.value)}
                />
                <select
                  className="border rounded-2xl px-4 py-3 w-full text-base md:text-lg text-gray-700"
                  value={qLevel}
                  onChange={(e) => setQLevel(e.target.value)}
                >
                  <option value="">Niveau</option>
                  <option value="Primaire">Primaire</option>
                  <option value="Collège">Collège</option>
                  <option value="Lycée">Lycée</option>
                  <option value="Supérieur">Supérieur</option>
                  <option value="Adulte">Adulte</option>
                </select>
                <button
                  type="submit"
                  className="bg-primary hover:bg-primary-dark text-white font-semibold rounded-2xl px-6 py-3 text-base md:text-lg"
                >
                  Rechercher
                </button>
              </form>

              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600">
                <span className="inline-flex items-center gap-2"><span className="text-xl">⭐</span> Avis vérifiés</span>
                <span className="inline-flex items-center gap-2"><span className="text-xl">🔒</span> Paiement sécurisé</span>
                <span className="inline-flex items-center gap-2"><span className="text-xl">⚡</span> Réservation rapide</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 🎟️ BANDEROLE INSCRIPTION - DESKTOP */}
      <div className="hidden lg:block bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 text-white py-3 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-4">
          <span className="flex items-center gap-2 font-semibold text-sm">
            <span className="text-xl">🎁</span>
            <span>Inscrivez-vous et obtenez <strong>-5 € sur votre premier cours</strong> — automatiquement après inscription !</span>
          </span>
          <div className="flex items-center gap-3">
            <a
              href="/register"
              className="bg-white text-emerald-700 font-bold text-xs px-4 py-1.5 rounded-full hover:bg-emerald-50 transition shadow"
            >
              S'inscrire gratuitement →
            </a>
          </div>
        </div>
      </div>

      {/* COMMENT ÇA MARCHE */}
      <section className="py-14 bg-gray-50 border-y">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-extrabold text-center text-gray-900 mb-2">Comment ça marche ?</h2>
          <p className="text-center text-gray-500 mb-10">Trouvez un prof et commencez en moins de 5 minutes.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '1', icon: '🔍', title: 'Cherchez un professeur', desc: 'Filtrez par matière, niveau, ville ou visio. Consultez les profils et les avis vérifiés des familles.' },
              { step: '2', icon: '💳', title: 'Réservez et payez', desc: 'Choisissez un créneau, payez par carte en toute sécurité — en une fois ou en 3 fois. Zéro cash, zéro chèque.' },
              { step: '3', icon: '🎓', title: 'Commencez les cours', desc: 'Rejoignez la visio directement depuis votre espace, ou retrouvez le prof à domicile. Résultats garantis.' },
            ].map(({ step, icon, title, desc }) => (
              <div key={step} className="bg-white rounded-2xl p-6 shadow-sm border text-center">
                <div className="w-12 h-12 rounded-full bg-primary text-white font-extrabold text-xl flex items-center justify-center mx-auto mb-3">{step}</div>
                <div className="text-3xl mb-2">{icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/search" className="inline-block bg-primary text-white font-bold px-8 py-3 rounded-full shadow hover:bg-primary/90 transition">
              Trouver un professeur →
            </Link>
          </div>
        </div>
      </section>

      {/* PROFESSEURS */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-6">
            <h2 className="text-2xl font-bold">Nos professeurs</h2>
            <Link to="/search" className="text-primary hover:underline">Tout voir</Link>
          </div>

          {loadingTeachers ? (
            <div className="py-12 text-center text-gray-500">Chargement des professeurs…</div>
          ) : teachers.length === 0 ? (
            <div className="py-12 text-center text-gray-400">Aucun professeur pour le moment.</div>
          ) : (
            <>
              {/* MOBILE : slider horizontal */}
              <div className="sm:hidden -mx-4 px-4 overflow-x-auto pb-2">
                <div className="flex gap-4 snap-x snap-mandatory">
                  {teachers.slice(0, 12).map((prof) => {
                    const { first, last } = getDisplayNameParts(prof);
                    const modes = getModesLabel(prof);
                    const rating = getRating(prof);
                    const reviewsCount = getReviewCount(prof);
                    const subject = getSubjectLabel(prof);
                    const bio = (prof.bio || '').trim();
                    const prices = getPriceLines(prof);
                    const visibleTeachers = (teachers || []).filter(t => t.offer_enabled !== false);

                    return (
                      <Link
                        key={prof.id}
                        to={`/profils/${prof.id}`}
                        className="snap-start shrink-0 w-[78%] max-w-[320px] relative"
                      >
                        <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
                          {reviewsCount >= 5 && <CertifiedBadge />}
                          <NewProfBadge teacher={prof} />
                        </div>
                        {/* IMAGE + NOM SUR IMAGE */}
                        <div className="relative h-60 bg-gray-100 rounded-3xl overflow-hidden">
                          <img
                            src={prof.avatarUrl || prof.photoURL || '/avatar-default.png'}
                            alt={prof.fullName || 'Professeur'}
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                          <div className="absolute left-4 right-4 bottom-4 text-white">
                            <div className="flex items-end justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-lg font-extrabold leading-none truncate">{first}</div>
                                {last && <div className="text-sm opacity-95 truncate">{last}</div>}
                              </div>

                              {modes && (
                                <span className="shrink-0 text-xs font-semibold px-3 py-1 rounded-full bg-white/20 border border-white/25 backdrop-blur">
                                  {modes}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* CONTENU */}
                        <div className="mt-3 px-1">
                          <div className="flex items-center gap-2 text-sm">
                            {rating > 0 ? (
                              <>
                                <span className="text-amber-600 font-semibold">⭐ {rating.toFixed(1)}</span>
                                <span className="text-gray-500">({reviewsCount} avis)</span>
                              </>
                            ) : (
                              <span className="text-gray-500">⭐ — (0 avis)</span>
                            )}
                          </div>

                          <div className="mt-2 text-sm text-gray-800">
                            <span className="font-semibold">{subject}</span>
                            {bio ? <span className="text-gray-600"> — {bio}</span> : null}
                          </div>

                          <div className="mt-3 space-y-1">                           
                            {prices.map((p) => (
                              <div key={p.label} className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">{p.label}</span>
                                <span className="font-extrabold text-primary">
                                  {p.price.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}/h
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* DESKTOP : grid */}
              <div className="hidden sm:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {teachers.slice(0, 5).map((prof) => {
                  const { first, last } = getDisplayNameParts(prof);
                  const modes = getModesLabel(prof);
                  const rating = getRating(prof);
                  const reviewsCount = getReviewCount(prof);
                  const subject = getSubjectLabel(prof);
                  const bio = (prof.bio || '').trim();
                  const prices = getPriceLines(prof);
                  const visibleTeachers = (teachers || []).filter(t => t.offer_enabled !== false);
                  
                  return (
                    <Link
                      key={prof.id}
                      to={`/profils/${prof.id}`}
                      className="group relative"
                    >
                      <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
                        {reviewsCount >= 5 && <CertifiedBadge />}
                        <NewProfBadge teacher={prof} />
                      </div>
                      {/* IMAGE + NOM SUR IMAGE */}
                      <div className="relative h-56 bg-gray-100 rounded-3xl overflow-hidden">
                        <img
                          src={prof.avatarUrl || prof.photoURL || '/avatar-default.png'}
                          alt={prof.fullName || 'Professeur'}
                          className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                        <div className="absolute left-4 right-4 bottom-4 text-white">
                          <div className="flex items-end justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-lg font-extrabold leading-none truncate">{first}</div>
                              {last && <div className="text-sm opacity-95 truncate">{last}</div>}
                            </div>

                            {modes && (
                              <span className="shrink-0 text-xs font-semibold px-3 py-1 rounded-full bg-white/20 border border-white/25 backdrop-blur">
                                {modes}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* RESTE EN DESSOUS (pas un "bloc") */}
                      <div className="mt-3 px-1">
                        <div className="flex items-center gap-2 text-sm">
                          {rating > 0 ? (
                            <>
                              <span className="text-amber-600 font-semibold">⭐ {rating.toFixed(1)}</span>
                              <span className="text-gray-500">({reviewsCount} avis)</span>
                            </>
                          ) : (
                            <span className="text-gray-500">⭐ — (0 avis)</span>
                          )}
                        </div>

                        <div className="mt-2 text-sm text-gray-800">
                          <span className="font-semibold">{subject}</span>
                          {bio ? <span className="text-gray-600"> — {bio}</span> : null}
                        </div>

                        <div className="mt-3 space-y-1">
                          {prices.map((p) => (
                            <div key={p.label} className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">{p.label}</span>
                              <span className="font-extrabold text-primary">
                                {p.price.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}/h
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── MATIÈRES POPULAIRES ──────────────────────────────────────── */}
      <section className="py-14 bg-white border-t">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">Matières populaires</h2>
              <p className="text-gray-500 mt-1 text-sm">Choisissez votre matière et trouvez le bon prof</p>
            </div>
            <Link to="/search" className="hidden sm:block text-primary text-sm font-semibold hover:underline">Tout parcourir →</Link>
          </div>

          {/* Mobile : scroll horizontal */}
          <div className="flex sm:hidden gap-3 overflow-x-auto pb-3 -mx-4 px-4 snap-x snap-mandatory">
            {categories.map((c) => {
              const n = getSubjectCount(c.label);
              const { accent = '#00804B' } = SUBJECT_CARD_STYLES[c.slug] || {};
              return (
                <button
                  key={c.slug}
                  onClick={() => navigate(`/search?subject=${encodeURIComponent(c.label)}`)}
                  className="snap-start shrink-0 w-32 bg-white rounded-2xl p-4 text-left border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-95 transition-all duration-150"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-2xl mb-3" style={{ background: accent + '18' }}>
                    {c.emoji}
                  </div>
                  <div className="font-bold text-gray-800 text-sm leading-tight">{c.label}</div>
                  {n > 0 && (
                    <div className="mt-1.5 text-[11px] font-medium text-gray-400">{n} prof{n > 1 ? 's' : ''}</div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Desktop : grille 8 colonnes */}
          <div className="hidden sm:grid grid-cols-4 md:grid-cols-8 gap-3">
            {categories.map((c) => {
              const n = getSubjectCount(c.label);
              const { accent = '#00804B' } = SUBJECT_CARD_STYLES[c.slug] || {};
              return (
                <button
                  key={c.slug}
                  onClick={() => navigate(`/search?subject=${encodeURIComponent(c.label)}`)}
                  className="group bg-white rounded-2xl p-4 text-left border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-gray-200 transition-all duration-200"
                >
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl mb-3 transition-transform group-hover:scale-110" style={{ background: accent + '18' }}>
                    {c.emoji}
                  </div>
                  <div className="font-bold text-gray-800 text-sm leading-tight">{c.label}</div>
                  {n > 0 && (
                    <div className="mt-1.5 text-[11px] font-medium text-gray-400">{n} prof{n > 1 ? 's' : ''}</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── EXPLORER PAR TERRITOIRE ──────────────────────────────────── */}
      <section className="py-14 bg-gray-50 border-t">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">Explorer par territoire</h2>
            <p className="text-gray-500 mt-1 text-sm">Des professeurs partout dans la Caraïbe, les DOM-TOM et au-delà</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {cityGroups.map((group) => {
              const tStyle = TERRITORY_STYLES[group.label] || { bg: 'linear-gradient(135deg,#00804B,#006038)', border: '#005a30' };
              const mainCity = group.cities[0];
              const extraCount = group.cities.length - 3;
              return (
                <div
                  key={group.label}
                  className="group relative rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-200 cursor-pointer hover:-translate-y-1"
                  style={{ background: tStyle.bg }}
                  onClick={() => navigate(`/search?city=${encodeURIComponent(mainCity)}`)}
                >
                  {/* Motif déco */}
                  <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/10 pointer-events-none" />
                  <div className="absolute -right-2 bottom-4 w-16 h-16 rounded-full bg-white/5 pointer-events-none" />

                  <div className="relative p-5">
                    {/* Emoji + nom */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <div className="text-3xl mb-1">{group.label.match(/[\u{1F1E0}-\u{1F1FF}][\u{1F1E0}-\u{1F1FF}]|\u{1F30D}|\u{1F30E}|\u{1F30F}|\u{1F310}|\u{1F5FA}|\u{1F3DD}/u)?.[0] || '🌍'}</div>
                        <div className="font-extrabold text-white text-base leading-tight">
                          {/* \u{FE0F} = sélecteur de variante emoji, à retirer explicitement :
                              il était auparavant introduit sans le vouloir par le « 🏝️ » littéral,
                              que la classe décomposait en 🏝 + FE0F. 🌐 et 🏝 sont déjà couverts
                              par la plage \u{1F30D}-\u{1F5FA}. */}
                          {group.label.replace(/^[\u{1F1E0}-\u{1F1FF}\u{1F30D}-\u{1F5FA}\u{FE0F}\s]+/u, '').trim() || group.label}
                        </div>
                      </div>
                      <div className="shrink-0 bg-white/20 text-white text-[11px] font-bold px-2 py-0.5 rounded-full mt-0.5">
                        {group.cities.length} ville{group.cities.length > 1 ? 's' : ''}
                      </div>
                    </div>

                    {/* Séparateur */}
                    <div className="h-px bg-white/20 mb-3" />

                    {/* Pills villes cliquables */}
                    <div className="flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {group.cities.slice(0, 3).map((city) => (
                        <button
                          key={city}
                          onClick={(e) => { e.stopPropagation(); navigate(`/search?city=${encodeURIComponent(city)}`); }}
                          className="text-[11px] font-semibold text-white bg-white/20 hover:bg-white/35 px-2.5 py-1 rounded-full transition"
                        >
                          {city}
                        </button>
                      ))}
                      {extraCount > 0 && (
                        <span className="text-[11px] font-semibold text-white/60 px-1 py-1">+{extraCount}</span>
                      )}
                    </div>

                    {/* CTA hover */}
                    <div className="mt-4 text-xs text-white/70 group-hover:text-white font-semibold flex items-center gap-1 transition-colors">
                      Voir les professeurs <span className="group-hover:translate-x-1 transition-transform inline-block">→</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMMENTAIRES réels seulement */}
      {hasRealReviews && (
        <section className="py-12 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6">Ils nous font confiance</h2>

            {/* Carrousel auto-scroll — mobile ET desktop */}
            <ReviewCarousel reviews={reviews} teacherMap={teacherMap} getSubjectLabel={getSubjectLabel} reviewBgClass={reviewBgClass} />
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div className="space-y-2">
            <h3 className="text-2xl font-bold">Vous cherchez un professeur ?</h3>
            <p className="text-white/90">Parcourez les profils et réservez en quelques clics.</p>
          </div>
          <div className="flex gap-3 md:justify-end">
            <Link to="/search" className="bg-white text-primary font-semibold px-5 py-3 rounded-xl hover:bg-white/90">Trouver un prof</Link>
          </div>
        </div>
      </section>

      {/* FAQ + À propos */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold mb-4">Cours particuliers aux Caraïbes : questions fréquentes</h2>
            <details className="border rounded-xl p-4 mb-3">
              <summary className="cursor-pointer font-semibold">Quels sont les tarifs moyens d'un cours ?</summary>
              <p className="mt-2 text-gray-600 text-sm">Les tarifs varient selon la matière, le niveau et l'expérience du professeur. Les prix sont fixés par les professeurs.</p>
            </details>
            <details className="border rounded-xl p-4 mb-3">
              <summary className="cursor-pointer font-semibold">Puis-je avoir un cours en visio ?</summary>
              <p className="mt-2 text-gray-600 text-sm">Les cours en visio sont disponibles ! Connectez-vous et profitez dès maintenant de cette fonctionnalité.</p>
            </details>
            <details className="border rounded-xl p-4">
              <summary className="cursor-pointer font-semibold">Comment sont gérés les paiements et remboursements ?</summary>
              <p className="mt-2 text-gray-600 text-sm">Le paiement est sécurisé. En cas de problème, un remboursement peut être demandé depuis votre espace, selon nos conditions.</p>
            </details>
          </div>

          <div className="bg-gray-50 border rounded-2xl p-6">
            <h3 className="font-semibold mb-2">À propos d'EduKaraib</h3>
            <p className="text-sm text-gray-700">
              EduKaraib est la plateforme locale de mise en relation entre élèves, parents et professeurs aux Caraïbes. Notre mission : rendre l'accompagnement pédagogique accessible à tous, avec une attention particulière aux réalités du territoire.
            </p>
          </div>
        </div>
      </section>

      {/* Derniers articles */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">Conseils & ressources</h2>
            <Link to="/blog" className="text-primary text-sm font-semibold hover:underline">Voir tous les articles →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {blogPosts.slice(0, 3).map(post => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group bg-gray-50 border border-gray-100 rounded-2xl p-5 hover:shadow-md transition flex flex-col gap-2"
              >
                <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full w-fit">{post.subject}</span>
                <h3 className="font-bold text-slate-900 leading-snug group-hover:text-primary transition text-sm">{post.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed line-clamp-2">{post.description}</p>
                <span className="text-xs text-gray-400 mt-auto">{post.readingTime} min de lecture</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}